'use client'

import Avatar from '@/components/avatar'
import Username from '@/components/username'
import { cn } from '@/util/cn'
import { GifOverlayProps } from '@giphy/react-components'
import { getContentOfGif, getCreatorOfGif, setGADataLayer } from 'analytics'
import Link from 'next/link'
import { useRouter } from 'next/navigation'
import { SyntheticEvent, useRef } from 'react'

export const AttrOverlay = ({
    gif,
    isHovered,
    asLink = false,
    attributionClassName,
}: GifOverlayProps & { asLink?: boolean; attributionClassName?: string }) => {
    const hasHovered = useRef(isHovered)
    const router = useRouter()
    if (isHovered) {
        // not rendering to avoid loading the avatar until hover
        hasHovered.current = true
    }
    const { user } = gif
    if (!user || !hasHovered.current) return null

    const trackClick = (event: SyntheticEvent) => {
        event.stopPropagation()
        setGADataLayer({
            event: 'attribution_open_click',
            options: {
                content: getContentOfGif(gif),
                creator: getCreatorOfGif(gif),
            },
        })
    }

    const attributionClassNames = cn(
        'pointer-events-auto absolute bottom-2.5 left-2.5 flex min-w-0 cursor-pointer gap-2 focus-visible:rounded-sm focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-giphyBlue',
        attributionClassName
    )
    const attribution = (
        <>
            <Avatar className="pointer-events-none shrink-0" user={user} size={32} />
            <Username
                noLink
                user={user}
                className="text-[16px] font-semibold text-giphyWhite group-hover:underline [&_svg]:fill-giphyWhite"
                alternateName={user.display_name}
            />
        </>
    )

    return (
        <div
            className={cn(
                'group pointer-events-none transition-opacity ease-in',
                isHovered ? 'opacity-100' : 'opacity-0'
            )}
        >
            <div
                className="pointer-events-none absolute inset-x-0 bottom-0 h-18.75 cursor-default"
                style={{
                    background: `linear-gradient(rgba(0, 0, 0, 0), rgba(18, 18, 18, 0.6))`,
                }}
            />
            {asLink ? (
                <Link
                    href={`/${user.username}`}
                    className={attributionClassNames}
                    data-testid="gif-attribution"
                    onClick={trackClick}
                >
                    {attribution}
                </Link>
            ) : (
                <div
                    className={attributionClassNames}
                    data-testid="gif-attribution"
                    onClick={(event) => {
                        trackClick(event)
                        router.push(`/${user.username}`)
                    }}
                >
                    {attribution}
                </div>
            )}
        </div>
    )
}
